import { useForm } from 'react-hook-form'
import { Col, Form, Row } from 'reactstrap'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import ComponentBox from '../../components/ComponentBox/ComponentBox'
import ICSDateRange from '../../components/ICSDateRange/ICSDateRange'
import ICSButton from '../../components/ICS/Button/ICSButton'
import { VehicleCertQuery } from '../../services/endpoints/service29'

dayjs.extend(utc)

const API_DATE_FORMAT = 'YYYY/MM/DD HH:mm:ss'

export type CertFilterField =
  | 'vehicleVin'
  | 'deviceSerial'
  | 'requestedByUsername'
  | 'issuedByUsername'
  | 'revokedByUsername'

export type CertDateField =
  | 'requestedAt'
  | 'issuedAt'
  | 'certExpiresAt'
  | 'deletedAt'

export type CertDateRange = { field: CertDateField; label: string }

type DateBounds = { start: Date | null; end: Date | null }

export type CertFilterValues = Record<CertFilterField, string> & {
  status: string
  dates: Record<CertDateField, DateBounds>
}

export type CertStatusOption = { value: string; label: string }

const FIELD_CONFIG: Record<
  CertFilterField,
  { label: string; placeholder: string }
> = {
  vehicleVin: { label: 'VIN', placeholder: 'e.g. 1HGCM82633A' },
  deviceSerial: { label: 'Device Serial', placeholder: 'e.g. D22660' },
  requestedByUsername: {
    label: 'Requested By',
    placeholder: 'e.g. jdoe@intrepidcs.com'
  },
  issuedByUsername: {
    label: 'Issued By',
    placeholder: 'e.g. jdoe@intrepidcs.com'
  },
  revokedByUsername: {
    label: 'Revoked By',
    placeholder: 'e.g. jdoe@intrepidcs.com'
  }
}

const EMPTY_BOUNDS: DateBounds = { start: null, end: null }

const DEFAULT_VALUES: CertFilterValues = {
  vehicleVin: '',
  deviceSerial: '',
  requestedByUsername: '',
  issuedByUsername: '',
  revokedByUsername: '',
  status: '',
  dates: {
    requestedAt: EMPTY_BOUNDS,
    issuedAt: EMPTY_BOUNDS,
    certExpiresAt: EMPTY_BOUNDS,
    deletedAt: EMPTY_BOUNDS
  }
}

/**
 * Maps the form's date bounds to `<field>After`/`<field>Before` params formatted as
 * `yyyy/MM/dd HH:mm:ss` in UTC. The bounds come from ICSDateRange, which already
 * interprets the picked wall-clock time in the user's preferred timezone, so the
 * Date instants only need converting to UTC here.
 */
export const dateRangeParams = (
  dates: CertFilterValues['dates']
): Partial<VehicleCertQuery> =>
  Object.entries(dates).reduce<Record<string, string | undefined>>(
    (params, [field, { start, end }]) => {
      params[`${field}After`] = start
        ? dayjs.utc(start).format(API_DATE_FORMAT)
        : undefined
      params[`${field}Before`] = end
        ? dayjs.utc(end).format(API_DATE_FORMAT)
        : undefined
      return params
    },
    {}
  )

type Props = {
  fields: CertFilterField[]
  statusOptions: CertStatusOption[]
  dateRanges?: CertDateRange[]
  onSearch: (values: CertFilterValues) => void
}

const CertFilters = ({
                       fields,
                       statusOptions,
                       dateRanges = [],
                       onSearch
                     }: Props) => {
  const { handleSubmit, control, register, reset } = useForm<CertFilterValues>({
    defaultValues: DEFAULT_VALUES
  })

  const submit = (values: CertFilterValues) => onSearch(values)

  const clear = () => {
    reset(DEFAULT_VALUES)
    onSearch(DEFAULT_VALUES)
  }

  return (
    <ComponentBox title="Search Criteria" accordion={true}>
      <Form onSubmit={handleSubmit(submit)}>
        <Row>
          {fields.map((field) => (
            <Col xs={12} md={6} key={field}>
              <div className="spacer">
                <label
                  className="cert-filter-label"
                  htmlFor={`cert-filter-${field}`}
                >
                  {FIELD_CONFIG[field].label}
                </label>
                <input
                  id={`cert-filter-${field}`}
                  className="form-control"
                  placeholder={FIELD_CONFIG[field].placeholder}
                  {...register(field)}
                />
              </div>
            </Col>
          ))}
          <Col xs={12} md={6}>
            <div className="spacer">
              <label className="cert-filter-label" htmlFor="cert-filter-status">
                Status
              </label>
              <select
                id="cert-filter-status"
                className="form-control"
                {...register('status')}
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </Col>
          {dateRanges.map(({ field, label }) => (
            <Col xs={12} md={6} key={field}>
              <div className="spacer">
                <label className="cert-filter-label">{label}</label>
                <div className="cert-date-range">
                  <ICSDateRange
                    name={`dates.${field}`}
                    control={control}
                    label={label}
                  />
                </div>
              </div>
            </Col>
          ))}
        </Row>
        <Row className="cert-filter-actions">
          <Col xs={12}>
            <ICSButton
              icon="search"
              text="Search"
              type="success"
              useSubmitBehavior={true}
            />
            <ICSButton text="Clear" type="normal" onClick={clear} />
          </Col>
        </Row>
      </Form>
    </ComponentBox>
  )
}

export default CertFilters
